import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getGatewayClient } from './GatewayClient.js';
import { DesktopPanel } from './AssistantPanel.jsx';
import { HubMarkdownBody } from './shared.jsx';
import { GatewayPanelV2 } from './GatewayThread.jsx';

// v2 is the default; explicit opt-out via ?gw-v2=0 or
// localStorage.hub-gateway-v2='0' temporarily falls back to the legacy
// reducer-based panel.
function isGatewayV2Enabled() {
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('gw-v2') === '0') return false;
    if (qs.get('gw-v2') === '1') return true;
    if (localStorage.getItem('hub-gateway-v2') === '0') return false;
  } catch {}
  return true;
}

// ── Session taxonomy ─────────────────────────────────────────────────────
const DEFAULT_SESSION_KEY = 'agent:main:main';
const HISTORY_LIMIT = 50;

const SESSION_KINDS = [
  { kind: 'main',       icon: '🏠', label: 'Main' },
  { kind: 'heartbeat',  icon: '💓', label: 'Heartbeat' },
  { kind: 'mattermost', icon: '💬', label: 'Mattermost' },
  { kind: 'cron',       icon: '⏰', label: 'Cron' },
  { kind: 'openai',     icon: '🤖', label: 'OpenAI' },
  { kind: 'responses',  icon: '📤', label: 'Responses' },
  { kind: 'other',      icon: '·',  label: 'Other' },
];
const SESSION_KIND_BY_KEY = Object.fromEntries(SESSION_KINDS.map((k) => [k.kind, k]));
const DEFAULT_ENABLED_KINDS = ['main', 'heartbeat', 'mattermost', 'cron'];
const ENABLED_KINDS_STORAGE_KEY = 'hub-gateway-session-kinds';

function classifySessionKind(key) {
  if (!key) return 'other';
  const tail = String(key).replace(/^agent:main:/, '');
  if (tail === 'main') return 'main';
  if (tail === 'main:heartbeat' || tail.endsWith(':heartbeat')) return 'heartbeat';
  if (tail.startsWith('mattermost:')) return 'mattermost';
  if (tail.startsWith('cron:')) return 'cron';
  if (tail.startsWith('openai:')) return 'openai';
  if (tail.startsWith('openresponses-user:') || tail.startsWith('openresponses:')) return 'responses';
  return 'other';
}

function shortSessionLabel(key) {
  if (!key) return '(no session)';
  return String(key).replace(/^agent:main:/, '');
}

function sessionOptionLabel(s) {
  const kind = classifySessionKind(s?.key);
  const icon = SESSION_KIND_BY_KEY[kind]?.icon || '·';
  const title = String(s?.title || '').replace(/\s+/g, ' ').trim();
  const truncTitle = title.length > 36 ? title.slice(0, 35) + '…' : title;
  const keyTail = shortSessionLabel(s?.key);
  const tailShort = keyTail.length > 42 ? keyTail.slice(0, 41) + '…' : keyTail;
  if (truncTitle) return `${icon} ${tailShort} — ${truncTitle}`;
  return `${icon} ${tailShort}`;
}

function STATUS_PILL(state) {
  switch (state) {
    case 'ready':       return { color: 'green',  label: 'connected' };
    case 'handshaking': return { color: 'yellow', label: 'handshaking' };
    case 'connecting':  return { color: 'yellow', label: 'connecting' };
    case 'closed':      return { color: 'red',    label: 'closed' };
    case 'error':       return { color: 'red',    label: 'error' };
    case 'stopped':     return { color: 'red',    label: 'stopped' };
    default:            return { color: 'gray',   label: 'idle' };
  }
}

// ── Content / segment conversion ─────────────────────────────────────────
// Canonical messages come from chat.history with typed content blocks. We
// fan them out into render segments so the bubble can show text, model
// thinking, tool calls and tool results inline in source order.
function contentBlocksToSegments(content) {
  const segments = [];
  if (!Array.isArray(content)) return segments;
  for (const c of content) {
    if (!c || typeof c !== 'object') continue;
    switch (c.type) {
      case 'text': {
        const text = typeof c.text === 'string' ? c.text : '';
        if (!text) break;
        const last = segments[segments.length - 1];
        if (last && last.type === 'text') last.text += text;
        else segments.push({ type: 'text', text });
        break;
      }
      case 'thinking':
        if (typeof c.thinking === 'string' && c.thinking.trim()) {
          segments.push({ type: 'thinking', text: c.thinking });
        }
        break;
      case 'toolCall':
        segments.push({
          type: 'tool',
          name: c.name || 'tool',
          args: c.arguments,
          callId: c.id || null,
        });
        break;
      case 'toolResult':
      case 'tool_result': {
        const raw = c.content ?? c.output ?? c.result ?? null;
        let text;
        if (raw == null) text = '';
        else if (typeof raw === 'string') text = raw;
        else text = JSON.stringify(raw);
        segments.push({
          type: 'tool_result',
          text,
          callId: c.tool_use_id || c.callId || c.id || null,
        });
        break;
      }
      default:
        // Unknown block — ignore in the rendered transcript.
        break;
    }
  }
  return segments;
}

function historyMessageToBubble(msg, idx) {
  const role = msg?.role === 'user' ? 'user' : 'assistant';
  const segments = contentBlocksToSegments(msg?.content);
  const text = segments.filter((s) => s.type === 'text').map((s) => s.text).join('');
  const id = `h-${role}-${idx}`;
  return {
    id,
    role,
    text,
    segments,
    done: true,
    progress: [],
    source: 'history',
  };
}

function historyPayloadToMessages(payload) {
  const arr = Array.isArray(payload?.messages) ? payload.messages : [];
  return arr.map((m, i) => historyMessageToBubble(m, i));
}

// Live progress events — surfaced as inline segments inside the in-flight
// assistant bubble until chat.history is refetched after final.
function liveProgressLabel(frame) {
  if (!frame) return null;
  const ev = frame.event;
  const p = frame.payload || {};
  if (ev === 'agent' && p.stream === 'tool') {
    const name = p.data?.name || p.data?.tool || 'tool';
    const phase = p.data?.phase || 'running';
    return { label: `${name} · ${phase}` };
  }
  if (ev === 'agent' && p.stream === 'lifecycle') {
    const phase = p.data?.phase || 'agent';
    if (phase === 'start') return null;
    return { label: `${phase}` };
  }
  if (ev === 'session.tool') {
    const name = p.tool?.name || p.name || 'tool';
    const status = p.status || (p.done ? 'done' : 'running');
    return { label: `${name} · ${status}` };
  }
  if (ev === 'exec.approval.requested') return { label: '🛑 approval requested' };
  if (ev === 'exec.approval.resolved')  return { label: '✅ approval resolved' };
  return null;
}

// ── Reducer ──────────────────────────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {
    case 'replace':
      return { ...state, messages: Array.isArray(action.messages) ? action.messages : [] };
    case 'append':
      return { ...state, messages: [...state.messages, action.message] };
    case 'patch_assistant': {
      const idx = state.messages.findIndex((m) => m.id === action.id);
      if (idx === -1) {
        const segs = action.text ? [{ type: 'text', text: action.text }] : [];
        return {
          ...state,
          messages: [
            ...state.messages,
            {
              id: action.id, role: 'assistant',
              text: action.text || '', done: !!action.done,
              segments: segs, progress: [], source: 'live',
            },
          ],
        };
      }
      const next = state.messages.slice();
      const prev = next[idx];
      const prevText = prev.text || '';
      const nextText = action.text || '';
      const segments = Array.isArray(prev.segments) ? prev.segments.slice() : [];
      if (nextText !== prevText) {
        const last = segments[segments.length - 1];
        const delta = nextText.startsWith(prevText) ? nextText.slice(prevText.length) : null;
        if (delta != null && delta.length) {
          if (last && last.type === 'text') segments[segments.length - 1] = { ...last, text: last.text + delta };
          else segments.push({ type: 'text', text: delta });
        } else if (delta == null) {
          segments.length = 0;
          segments.push({ type: 'text', text: nextText });
        }
      }
      next[idx] = { ...prev, text: nextText, done: !!action.done, segments };
      return { ...state, messages: next };
    }
    case 'append_progress': {
      let msgs = state.messages.slice();
      let idx = -1;
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant' && !msgs[i].done) { idx = i; break; }
      }
      if (idx === -1) return state; // no in-flight bubble to attach to
      const prev = msgs[idx];
      const segs = Array.isArray(prev.segments) ? prev.segments.slice() : [];
      const lastSeg = segs[segs.length - 1];
      if (lastSeg && lastSeg.type === 'progress' && lastSeg.label === action.label) return state;
      segs.push({ type: 'progress', label: action.label });
      msgs[idx] = { ...prev, segments: segs };
      return { ...state, messages: msgs };
    }
    case 'mark_aborted': {
      let msgs = state.messages.slice();
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant' && !msgs[i].done) {
          msgs[i] = { ...msgs[i], done: true, aborted: true };
          break;
        }
      }
      return { ...state, messages: msgs };
    }
    default:
      return state;
  }
}

// ── Render helpers ───────────────────────────────────────────────────────
function formatElapsed(ms) {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.floor(s % 60)}s`;
}

function truncateArgsForLabel(args) {
  if (args == null) return '';
  let raw;
  try { raw = typeof args === 'string' ? args : JSON.stringify(args); } catch { raw = String(args); }
  raw = raw.replace(/\s+/g, ' ').trim();
  if (raw.length > 60) raw = raw.slice(0, 57) + '…';
  return raw;
}

function ToolSegment({ seg }) {
  const [open, setOpen] = useState(false);
  const summary = truncateArgsForLabel(seg.args);
  return (
    <details
      className="hub-gateway-segment-tool"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary>
        <span aria-hidden="true" style={{ display: 'inline-flex' }}>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.1-.5-.5-2.1 2.6-2.6Z" />
          </svg>
        </span>
        <span className="hub-gateway-segment-tool-name">{seg.name || 'tool'}</span>
        {summary && <span className="hub-gateway-segment-tool-args">({summary})</span>}
      </summary>
      <pre className="hub-gateway-segment-tool-full">
        {typeof seg.args === 'string' ? seg.args : JSON.stringify(seg.args, null, 2)}
      </pre>
    </details>
  );
}

function ToolResultSegment({ seg }) {
  const [open, setOpen] = useState(false);
  const text = typeof seg.text === 'string' ? seg.text : '';
  const summary = text.length > 80 ? text.slice(0, 79).replace(/\s+/g, ' ') + '…' : text.replace(/\s+/g, ' ');
  return (
    <details
      className="hub-gateway-segment-tool-result"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary>
        <span aria-hidden="true">📤</span>
        <span className="hub-gateway-segment-tool-result-summary">{summary || '(empty)'}</span>
      </summary>
      <pre className="hub-gateway-segment-tool-result-full">{text}</pre>
    </details>
  );
}

function AssistantBubble({ message }) {
  const streaming = !message.done;
  const segments = useMemo(() => {
    if (Array.isArray(message.segments) && message.segments.length) return message.segments;
    return message.text ? [{ type: 'text', text: message.text }] : [];
  }, [message.segments, message.text]);

  // Tick a few times a second so the elapsed counter is live without
  // depending on per-message dispatches.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!streaming) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 200);
    return () => clearInterval(id);
  }, [streaming]);
  const elapsedMs = message.pendingSince ? Date.now() - message.pendingSince : 0;
  const charCount = (message.text || '').length;

  const hasContent = segments.length > 0;

  return (
    <div className={`hub-gateway-msg is-assistant${message.done ? ' is-done' : ''}${streaming ? ' is-streaming' : ''}${message.aborted ? ' is-aborted' : ''}`}>
      {streaming && !hasContent && (
        <div className="hub-gateway-pending" aria-label="thinking">
          <span className="hub-gateway-pending-label">Thinking</span>
          <span className="hub-gateway-thinking-dots" aria-hidden="true">
            <span /><span /><span />
          </span>
        </div>
      )}
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        if (seg.type === 'progress') {
          return (
            <div key={`p-${i}`} className="hub-gateway-segment-progress">
              <span className="hub-gateway-segment-progress-label">{seg.label}</span>
            </div>
          );
        }
        if (seg.type === 'thinking') {
          return (
            <div key={`th-${i}`} className="hub-gateway-segment-thinking">
              <span className="hub-gateway-segment-thinking-text">{seg.text}</span>
            </div>
          );
        }
        if (seg.type === 'tool') {
          return <ToolSegment key={`tc-${i}`} seg={seg} />;
        }
        if (seg.type === 'tool_result') {
          return <ToolResultSegment key={`tr-${i}`} seg={seg} />;
        }
        return (
          <div
            key={`t-${i}`}
            className={`hub-gateway-msg-body${streaming ? ' hub-gateway-msg-streaming' : ''}`}
          >
            <HubMarkdownBody text={seg.text} streaming={streaming && isLast} />
            {streaming && isLast && <span className="hub-gateway-cursor" aria-hidden="true">▍</span>}
          </div>
        );
      })}
      {!streaming && segments.length === 0 && (
        <div className="hub-gateway-msg-body">{'(no response)'}</div>
      )}
      {streaming && message.pendingSince && (
        <div className="hub-gateway-meta" aria-label="run progress">
          <span className="hub-gateway-meta-dot" aria-hidden="true" />
          {formatElapsed(elapsedMs)}
          {charCount > 0 && <span className="hub-gateway-meta-sep">·</span>}
          {charCount > 0 && <span>{charCount} chars</span>}
        </div>
      )}
    </div>
  );
}

function UserBubble({ message }) {
  const segments = Array.isArray(message.segments) && message.segments.length
    ? message.segments
    : (message.text ? [{ type: 'text', text: message.text }] : []);
  return (
    <div className="hub-gateway-msg is-user">
      {segments.map((seg, i) => (
        seg.type === 'text'
          ? <pre key={i} className="hub-gateway-msg-body">{seg.text}</pre>
          : null
      ))}
    </div>
  );
}

// ── Panel ───────────────────────────────────────────────────────────────
export function GatewayPanel() {
  // Feature flag — when on, render the assistant-ui-based v2 panel instead
  // of the legacy reducer-driven one. Toggle via ?gw-v2=1 or by setting
  // localStorage.hub-gateway-v2 = '1'.
  const useV2 = useMemo(() => isGatewayV2Enabled(), []);
  if (useV2) return <GatewayPanelV2 />;

  const client = useMemo(() => getGatewayClient(), []);
  const [open, setOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [state, setState] = useState({ state: 'idle' });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [conv, dispatch] = useReducer(reducer, undefined, () => ({ messages: [] }));
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const [sessionKey, setSessionKey] = useState(DEFAULT_SESSION_KEY);
  const [sessionList, setSessionList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [enabledKinds, setEnabledKinds] = useState(() => {
    try {
      const raw = localStorage.getItem(ENABLED_KINDS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return new Set(parsed);
      }
    } catch {}
    return new Set(DEFAULT_ENABLED_KINDS);
  });
  useEffect(() => {
    try { localStorage.setItem(ENABLED_KINDS_STORAGE_KEY, JSON.stringify([...enabledKinds])); } catch {}
  }, [enabledKinds]);
  const toggleKind = useCallback((kind) => {
    setEnabledKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }, []);

  const groupedSessions = useMemo(() => {
    const byKind = new Map();
    for (const s of sessionList) {
      const k = classifySessionKind(s.key);
      if (!enabledKinds.has(k) && s.key !== sessionKey) continue;
      if (!byKind.has(k)) byKind.set(k, []);
      byKind.get(k).push(s);
    }
    return SESSION_KINDS
      .map((meta) => ({ meta, sessions: byKind.get(meta.kind) || [] }))
      .filter((g) => g.sessions.length > 0);
  }, [sessionList, enabledKinds, sessionKey]);

  // Inflight = last assistant bubble with done:false (set optimistically on
  // send, completed by streaming events + chat.history canonicalisation).
  const inflight = useMemo(() => {
    for (let i = conv.messages.length - 1; i >= 0; i--) {
      const m = conv.messages[i];
      if (m.role === 'assistant' && !m.done) return m;
      if (m.role === 'user') return null;
    }
    return null;
  }, [conv.messages]);
  const sessionBusy = busy || !!inflight;

  // ── Source of truth: chat.history. Fetch on ready and on session switch.
  const loadHistory = useCallback(async (key) => {
    const target = key || sessionKey;
    setHistoryLoading(true);
    try {
      const payload = await client.chatHistory({ sessionKey: target, limit: HISTORY_LIMIT });
      const messages = historyPayloadToMessages(payload);
      dispatch({ type: 'replace', messages });
    } catch (err) {
      console.warn('chat.history failed:', err?.message || err);
    } finally {
      setHistoryLoading(false);
    }
  }, [client, sessionKey]);

  const refreshSessions = useCallback(async () => {
    try {
      const payload = await client.sessionsList({ limit: 30 });
      const arr = Array.isArray(payload?.sessions) ? payload.sessions : [];
      setSessionList(arr);
    } catch {}
  }, [client]);

  // ── Connect / state plumbing
  useEffect(() => {
    function onState(e) {
      setState({ state: e.detail.state, detail: e.detail });
      const nextKey = e.detail.sessionKey;
      if (nextKey && nextKey !== sessionKey) setSessionKey(nextKey);
    }
    function onStream(e) {
      dispatch({
        type: 'patch_assistant',
        id: e.detail.messageId,
        text: e.detail.text,
        done: e.detail.done,
      });
      if (e.detail.done) {
        setBusy(false);
        // Canonicalise the bubble's content (tool calls, thinking blocks)
        // by re-fetching the authoritative history.
        loadHistory(sessionKey);
      }
    }
    function onEvent(e) {
      const lbl = liveProgressLabel(e.detail);
      if (lbl) dispatch({ type: 'append_progress', label: lbl.label });
    }
    client.addEventListener('state', onState);
    client.addEventListener('stream', onStream);
    client.addEventListener('event', onEvent);
    return () => {
      client.removeEventListener('state', onState);
      client.removeEventListener('stream', onStream);
      client.removeEventListener('event', onEvent);
    };
  }, [client, sessionKey, loadHistory]);

  // Open the WS lazily when the panel opens.
  useEffect(() => {
    if (!open) return;
    client.connect();
  }, [open, client]);

  // On ready: refresh sessions + load history for the active session.
  useEffect(() => {
    if (state.state !== 'ready') return;
    refreshSessions();
    loadHistory(sessionKey);
  }, [state.state, refreshSessions, loadHistory, sessionKey]);

  const switchSession = useCallback((nextKey) => {
    const target = String(nextKey || '').trim() || DEFAULT_SESSION_KEY;
    if (target === sessionKey) return;
    setSessionKey(target);
    setBusy(false);
    dispatch({ type: 'replace', messages: [] });
    loadHistory(target);
  }, [sessionKey, loadHistory]);

  const stop = useCallback(async () => {
    if (!inflight) return;
    try {
      await client.chatAbort({ sessionKey, runId: inflight.id });
    } catch {}
    dispatch({ type: 'mark_aborted' });
    setBusy(false);
    loadHistory(sessionKey);
  }, [inflight, client, sessionKey, loadHistory]);

  const resetSession = useCallback(async () => {
    if (sessionBusy) {
      try { window.hubToast?.error?.('Stop the current run before resetting'); } catch {}
      return;
    }
    const confirmed = window.confirm(
      `Reset session "${shortSessionLabel(sessionKey)}" on the gateway?\n\nThis deletes the conversation history on the OpenClaw side. Cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await client.sessionsReset({ key: sessionKey, reason: 'panel-clear' });
      try { window.hubToast?.success?.('Session reset'); } catch {}
    } catch (err) {
      try { window.hubToast?.error?.(`Reset failed: ${err.message || err}`); } catch {}
    }
    dispatch({ type: 'replace', messages: [] });
    loadHistory(sessionKey);
  }, [sessionKey, sessionBusy, client, loadHistory]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sessionBusy) return;
    if (state.state !== 'ready') return;
    setBusy(true);
    const runId = `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    dispatch({ type: 'append', message: { id: `u-${Date.now()}`, role: 'user', text, segments: [{ type: 'text', text }], done: true, progress: [], source: 'live' } });
    dispatch({ type: 'append', message: { id: runId, role: 'assistant', text: '', done: false, segments: [], progress: [], pendingSince: Date.now(), source: 'live' } });
    setInput('');
    // After Send the operator always wants the new bubble in view.
    requestAnimationFrame(() => scrollToBottom('auto'));
    try {
      await client.sendMessage(text, { runId, sessionKey });
    } catch (err) {
      const hint = err?.error?.details?.recommendedNextStep || err?.error?.code || '';
      const errText = `error: ${err.message || err}${hint ? ` (${hint})` : ''}`;
      dispatch({ type: 'patch_assistant', id: runId, text: errText, done: true });
      setBusy(false);
    }
  }, [client, input, sessionBusy, state.state, sessionKey]);

  const onKey = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }, [send]);

  // Imperative scroll helper — used after history load and after the
  // operator sends a message, where we always want the latest content in
  // view regardless of where the scroll was.
  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Stream-tail auto-scroll: smoothly follows the bottom only when the
  // operator is already near it (gives them room to scroll back without
  // being yanked away). The "always go bottom" cases (open / history
  // load / send) are handled by explicit scrollToBottom calls.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > 120) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [conv.messages.length, conv.messages.map((m) => (m.text || '') + ((m.segments && m.segments.length) || 0)).join('|'), open]);

  // Jump straight to the bottom whenever a fresh batch of messages lands
  // (history fetch completes / session switch / panel open).
  useEffect(() => {
    if (!open) return;
    if (historyLoading) return;
    if (conv.messages.length === 0) return;
    // Defer until after layout so scrollHeight reflects the new content.
    const id = requestAnimationFrame(() => scrollToBottom('auto'));
    return () => cancelAnimationFrame(id);
  }, [open, historyLoading, sessionKey, conv.messages.length === 0, scrollToBottom]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const pill = STATUS_PILL(state.state);
  const isReady = state.state === 'ready';

  return (
    <>
      {desktopOpen && <DesktopPanel floating onClose={() => setDesktopOpen(false)} />}

      <button
        type="button"
        className={`hub-gateway-fab${open ? ' is-open' : ''}`}
        aria-label={open ? 'Close OpenClaw direct chat' : 'Open OpenClaw direct chat'}
        title="OpenClaw Direct (gateway)"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="hub-gateway-fab-glyph" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 5c4 2 7 6 9 11" />
            <path d="M9 4c3 2 5 5 6 9" />
            <path d="M14 3c2 1 3 3 4 6" />
            <path d="M3 19l4-2 4 2 4-2 4 2" />
          </svg>
        </span>
        <span className={`hub-gateway-fab-pill is-${pill.color}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="hub-gateway-panel" role="dialog" aria-label="OpenClaw Direct">
          <header className="hub-gateway-panel-head">
            <div className="hub-gateway-panel-title">
              <strong>OpenClaw Direct</strong>
              <span className={`hub-gateway-status is-${pill.color}`}>{pill.label}</span>
            </div>
            <select
              className="hub-gateway-session-combo"
              aria-label="Active session"
              title={sessionKey}
              value={sessionKey}
              onChange={(e) => switchSession(e.target.value)}
              disabled={!isReady}
            >
              {sessionList.length === 0 && (
                <option value={sessionKey}>{shortSessionLabel(sessionKey)}</option>
              )}
              {groupedSessions.map(({ meta, sessions }) => (
                <optgroup key={meta.kind} label={`${meta.icon} ${meta.label}`}>
                  {sessions.map((s) => (
                    <option key={s.key} value={s.key}>{sessionOptionLabel(s)}</option>
                  ))}
                </optgroup>
              ))}
              {sessionList.length > 0 && groupedSessions.every((g) => !g.sessions.some((s) => s.key === sessionKey)) && (
                <option value={sessionKey}>
                  {SESSION_KIND_BY_KEY[classifySessionKind(sessionKey)]?.icon || '·'} {shortSessionLabel(sessionKey)}
                </option>
              )}
            </select>
            {inflight && (
              <button
                type="button"
                className="hub-gateway-stop"
                aria-label="Stop current run"
                title="Stop current run"
                onClick={stop}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
                <span>STOP</span>
              </button>
            )}
            <button
              type="button"
              className="hub-gateway-clear"
              aria-label="Reset this session on the gateway"
              title="Reset this session on the gateway"
              onClick={resetSession}
              disabled={!isReady || sessionBusy || conv.messages.length === 0}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </button>
            <button
              type="button"
              className={`hub-gateway-vnc${desktopOpen ? ' is-open' : ''}`}
              aria-label={desktopOpen ? 'Close VNC' : 'Open VNC'}
              title={desktopOpen ? 'Close VNC' : 'Open VNC'}
              onClick={() => setDesktopOpen((v) => !v)}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="12" rx="2" />
                <path d="M8 20h8M12 16v4" />
              </svg>
              <span>VNC</span>
            </button>
            <button
              type="button"
              className="hub-gateway-panel-close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >×</button>
          </header>

          <div className="hub-gateway-session-filters" role="toolbar" aria-label="Session type filters">
            {SESSION_KINDS.filter((k) => k.kind !== 'other').map((meta) => {
              const active = enabledKinds.has(meta.kind);
              return (
                <button
                  key={meta.kind}
                  type="button"
                  className={`hub-gateway-session-filter${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  title={`${active ? 'Hide' : 'Show'} ${meta.label} sessions`}
                  onClick={() => toggleKind(meta.kind)}
                >
                  <span aria-hidden="true">{meta.icon}</span>
                  <span className="hub-gateway-session-filter-label">{meta.label}</span>
                </button>
              );
            })}
          </div>

          <div className="hub-gateway-messages" ref={scrollRef}>
            {!historyLoading && conv.messages.length === 0 && (
              <div className="hub-gateway-empty">
                Send a message and OpenClaw will reply, streaming text and
                tool activity inline.
              </div>
            )}
            {historyLoading && conv.messages.length === 0 && (
              <div className="hub-gateway-empty">Loading session…</div>
            )}
            {conv.messages.map((m) => (
              m.role === 'assistant'
                ? <AssistantBubble key={m.id} message={m} />
                : <UserBubble key={m.id} message={m} />
            ))}
            {state.state === 'error' && state.detail?.error && (
              <div className="hub-gateway-msg is-system">
                <pre className="hub-gateway-msg-body">connection error: {state.detail.error}</pre>
              </div>
            )}
          </div>

          <footer className="hub-gateway-composer">
            <textarea
              ref={inputRef}
              className="hub-gateway-input"
              placeholder={
                !isReady ? 'connecting…' :
                inflight ? 'Agent is responding… press STOP to interrupt' :
                'Send a message…'
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={2}
              disabled={sessionBusy || !isReady}
            />
            <button
              type="button"
              className="hub-gateway-send"
              onClick={send}
              disabled={sessionBusy || !input.trim() || !isReady}
              title={inflight ? 'Wait for the current run to finish, or press STOP' : 'Send'}
            >Send</button>
          </footer>
        </div>
      )}
    </>
  );
}
